/**
 * The route a train runs, and the arithmetic that walks it.
 *
 * ## What the original does — recovered, not invented
 *
 * Red Baron's train is object **class 5**, and everything below is a
 * transcription of it (full evidence with offsets in
 * docs/game/ground-traffic.md; the opcode grammar in
 * docs/formats/missions-campaign.md §"The scenario object stream"):
 *
 * - **The record is a pair of endpoints.** Scenario opcode 5 reads 25 bytes:
 *   `int32 startX, startY, startZ, int32 endX, endY, endZ, uint8 side`.
 * - **The constructor precomputes the route** (`pobj.c+0x424`, file `0x1A0A4`):
 *   it stores both points, then derives the route's length into `obj+0x7c` and
 *   its bearing into `obj+0x18`. Nothing else about the path is stored — there
 *   are no waypoints and no curve.
 * - **The mover is a linear interpolation** (`s\intel\move.c`, file `0x11621`):
 *   each tick it adds `speed * dt` to a 32-bit *distance travelled* accumulator
 *   at `obj+0x78`, compares it against the stored length, and writes
 *   `lerp(start, end, travelled / length)` into the object's world position.
 * - **Speed is one global**, `ds:0x4d42`, read fresh into `obj+0x74` every tick
 *   for every train. It is not per-train and not per-mission.
 * - **At the far end the train retires**: `obj[0x22] |= 0x8000`, speed zeroed,
 *   smoke plume stopped. It is *not* a kill — retiring and being destroyed are
 *   different bits (`0x8000` vs `0x40`), and only the second is a kill.
 *
 * So a train does not follow a railway. It runs a straight line between two
 * authored points across open country, because **the game has no railway to
 * follow**: the terrain's linear features are classed by palette index alone —
 * rivers at 4, roads at 67/68/70 — and there is no rail class in the `.WLD`
 * data, in `MAPn.DAT`, or anywhere else (docs/formats/world-terrain.md §7.2).
 * Anyone reaching for "put the train on the rail line" will not find one.
 *
 * ## What is port-side, and why
 *
 * The original's endpoints come from a mission compiler in the shell
 * (`PS.EXE` writes `SCRIPT.DAT`) that this port does not have and is not
 * porting. So **route placement is ours**, drawn from the mission seed through
 * the existing `mission` stream so a given seed always lays the same track —
 * the same convention every other generated placement already uses. The
 * mechanics above are not ours and must not drift.
 */

import type { Vec3 } from "../flight/math3d.ts";

/** A train's whole path: two points, exactly as the 25-byte record carries them. */
export interface GroundRoute {
  readonly start: Vec3;
  readonly end: Vec3;
  /** Ground-plane length, metres. The original precomputes this too (`obj+0x7c`). */
  readonly lengthM: number;
  /** Ground-plane bearing, radians. The original precomputes this too (`obj+0x18`). */
  readonly headingRad: number;
}

/**
 * The mission `x`/`z` plane is the ground; `y` is altitude. That matches the
 * original's own route records, whose third component is zero in both shipped
 * `SCRIPT.DAT` entries — a train's path is flat.
 */
export function groundRoute(start: Vec3, end: Vec3): GroundRoute {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  return {
    start,
    end,
    lengthM: Math.hypot(dx, dz),
    // Same convention the rest of the port uses for a heading in the x/z plane.
    headingRad: Math.atan2(dx, dz),
  };
}

/** Where a train sits after covering `travelledM` of its route. */
export function routePosition(route: GroundRoute, travelledM: number): Vec3 {
  if (route.lengthM <= 0) return route.start;
  const t = Math.min(1, Math.max(0, travelledM / route.lengthM));
  return {
    x: route.start.x + (route.end.x - route.start.x) * t,
    y: route.start.y + (route.end.y - route.start.y) * t,
    z: route.start.z + (route.end.z - route.start.z) * t,
  };
}

/** True once the train has run out of route — the original's `obj[0x22] |= 0x8000`. */
export function routeSpent(route: GroundRoute, travelledM: number): boolean {
  return travelledM >= route.lengthM;
}

/**
 * A route laid across the mission's ground plane from a seeded draw.
 *
 * `roll` is a `[0, 1)` supplier — the caller passes its own `mission` stream, so
 * this module stays free of RNG plumbing and a test can hand it a fixed
 * sequence. Four draws: where the line crosses the mission axis, which way it
 * runs, how long it is, and how far along it the train has already got when the
 * player arrives (a train the player meets mid-journey rather than always at its
 * starting post).
 */
export interface RouteDrawOptions {
  /** Mid-point of the band the route is laid in, in mission `z`. */
  readonly lineZ: number;
  /** Half-width of that band, metres. */
  readonly spreadM: number;
  /** Route length is drawn from this range, metres. */
  readonly minLengthM: number;
  readonly maxLengthM: number;
  /** Ground height the route sits at. */
  readonly groundY: number;
}

export interface DrawnRoute {
  readonly route: GroundRoute;
  /** How far along it the train starts — never the whole way, or it would retire at once. */
  readonly startTravelledM: number;
}

export function drawGroundRoute(roll: () => number, options: RouteDrawOptions): DrawnRoute {
  const crossZ = options.lineZ + (roll() * 2 - 1) * options.spreadM;
  // A quarter-turn of freedom either side of "along the front", so a route reads
  // as a line of communication running behind the lines rather than a random
  // scratch across the map.
  const bearing = (roll() * 2 - 1) * (Math.PI / 4);
  const lengthM = options.minLengthM + roll() * (options.maxLengthM - options.minLengthM);
  const half = lengthM / 2;
  const dx = Math.sin(bearing);
  const dz = Math.cos(bearing);
  const start: Vec3 = { x: -dx * half, y: options.groundY, z: crossZ - dz * half };
  const end: Vec3 = { x: dx * half, y: options.groundY, z: crossZ + dz * half };
  return {
    route: groundRoute(start, end),
    // Capped well short of the end so a drawn train always has road left to run.
    startTravelledM: roll() * lengthM * 0.5,
  };
}
